import { isSameDay, isSameYear } from 'date-fns';

import { DateFormat, transformDate } from 'shared/entities/date';
import {
  DatePickerDateFormat,
  DatePickerTriggerMode,
  DateRangeTriggerLabel,
  DateRangeTriggerLabelSegment,
  DateRangeTriggerLabelSegmentType,
  DateRangeTriggerLabelVariant
} from 'shared/newEntities/components/DatePicker';

const getDateRangeLabelVariant = (
  start: Date,
  end: Date,
  referenceDate: Date
): DateRangeTriggerLabelVariant => {
  if (isSameYear(start, referenceDate) && isSameYear(end, referenceDate)) {
    return isSameDay(start, end)
      ? DateRangeTriggerLabelVariant.currentYearSameDay
      : DateRangeTriggerLabelVariant.currentYearDifferentDays;
  }

  return DateRangeTriggerLabelVariant.withYear;
};

const segment = (
  type: DateRangeTriggerLabelSegmentType,
  value: string
): DateRangeTriggerLabelSegment => ({ type, value });

const buildTimeModeSegments = (
  interval: [Date, Date],
  referenceDate: Date,
  alwaysShowFullYear = false
): DateRangeTriggerLabelSegment[] => {
  const [start, end] = interval;
  const startTime = transformDate(start, DateFormat.time);
  const endTime = transformDate(end, DateFormat.time);

  if (alwaysShowFullYear) {
    const startDate = transformDate(start, DateFormat.dayMonthYear);
    const endDate = transformDate(end, DateFormat.dayMonthYear);

    if (isSameDay(start, end)) {
      return [
        segment(DateRangeTriggerLabelSegmentType.text, startDate),
        segment(DateRangeTriggerLabelSegmentType.text, ' '),
        segment(DateRangeTriggerLabelSegmentType.time, startTime),
        segment(DateRangeTriggerLabelSegmentType.text, ' - '),
        segment(DateRangeTriggerLabelSegmentType.time, endTime)
      ];
    }

    return [
      segment(DateRangeTriggerLabelSegmentType.text, startDate),
      segment(DateRangeTriggerLabelSegmentType.text, ' '),
      segment(DateRangeTriggerLabelSegmentType.time, startTime),
      segment(DateRangeTriggerLabelSegmentType.text, ' - '),
      segment(DateRangeTriggerLabelSegmentType.text, endDate),
      segment(DateRangeTriggerLabelSegmentType.text, ' '),
      segment(DateRangeTriggerLabelSegmentType.time, endTime)
    ];
  }

  const variant = getDateRangeLabelVariant(start, end, referenceDate);

  if (variant === DateRangeTriggerLabelVariant.currentYearSameDay) {
    return [
      segment(
        DateRangeTriggerLabelSegmentType.text,
        transformDate(start, DateFormat.dayMonth)
      ),
      segment(DateRangeTriggerLabelSegmentType.text, ' '),
      segment(DateRangeTriggerLabelSegmentType.time, startTime),
      segment(DateRangeTriggerLabelSegmentType.text, ' - '),
      segment(DateRangeTriggerLabelSegmentType.time, endTime)
    ];
  }

  if (variant === DateRangeTriggerLabelVariant.currentYearDifferentDays) {
    return [
      segment(
        DateRangeTriggerLabelSegmentType.text,
        transformDate(start, DateFormat.dayMonth)
      ),
      segment(DateRangeTriggerLabelSegmentType.text, ' '),
      segment(DateRangeTriggerLabelSegmentType.time, startTime),
      segment(DateRangeTriggerLabelSegmentType.text, ' - '),
      segment(
        DateRangeTriggerLabelSegmentType.text,
        transformDate(end, DateFormat.dayMonth)
      ),
      segment(DateRangeTriggerLabelSegmentType.text, ' '),
      segment(DateRangeTriggerLabelSegmentType.time, endTime)
    ];
  }

  return [
    segment(
      DateRangeTriggerLabelSegmentType.text,
      transformDate(start, DateFormat.date)
    ),
    segment(DateRangeTriggerLabelSegmentType.text, ' '),
    segment(DateRangeTriggerLabelSegmentType.time, startTime),
    segment(DateRangeTriggerLabelSegmentType.text, ' - '),
    segment(
      DateRangeTriggerLabelSegmentType.text,
      transformDate(end, DateFormat.date)
    ),
    segment(DateRangeTriggerLabelSegmentType.text, ' '),
    segment(DateRangeTriggerLabelSegmentType.time, endTime)
  ];
};

export const formatDateRangeTriggerLabel = (
  interval: [Date, Date] | null,
  _dateFormat: DatePickerDateFormat,
  referenceDate: Date = new Date(),
  alwaysShowFullYear = false
): string | null => {
  if (!interval) {
    return null;
  }

  const [start, end] = interval;

  if (alwaysShowFullYear) {
    if (isSameDay(start, end)) {
      return transformDate(start, DateFormat.dayMonthYear);
    }

    return `${transformDate(start, DateFormat.dayMonthYear)} - ${transformDate(
      end,
      DateFormat.dayMonthYear
    )}`;
  }

  const variant = getDateRangeLabelVariant(start, end, referenceDate);

  if (variant === DateRangeTriggerLabelVariant.currentYearSameDay) {
    return transformDate(start, DateFormat.dayMonth);
  }

  if (variant === DateRangeTriggerLabelVariant.currentYearDifferentDays) {
    return `${transformDate(start, DateFormat.dayMonth)} - ${transformDate(
      end,
      DateFormat.dayMonth
    )}`;
  }

  return `${transformDate(start, DateFormat.date)} - ${transformDate(
    end,
    DateFormat.date
  )}`;
};

export const buildDateRangeTriggerLabel = (
  interval: [Date, Date] | null,
  dateFormat: DatePickerDateFormat,
  referenceDate: Date = new Date(),
  alwaysShowFullYear = false
): DateRangeTriggerLabel | null => {
  if (!interval) {
    return null;
  }

  if (dateFormat === DateFormat.dayMonthYearTime) {
    return {
      mode: DatePickerTriggerMode.segments,
      segments: buildTimeModeSegments(
        interval,
        referenceDate,
        alwaysShowFullYear
      )
    };
  }

  const text = formatDateRangeTriggerLabel(
    interval,
    dateFormat,
    referenceDate,
    alwaysShowFullYear
  );

  return text
    ? {
        mode: DatePickerTriggerMode.plain,
        text
      }
    : null;
};

export const stringifyDateRangeTriggerLabel = (
  label: DateRangeTriggerLabel | null
): string | null => {
  if (!label) {
    return null;
  }

  if (label.mode === DatePickerTriggerMode.plain) {
    return label.text;
  }

  return label.segments.map(({ value }) => value).join('');
};
